// ─── XP / gem / leaderboard settings in the settings tab (V6-p) ──────────────
//
// Pure data and functions, kept out of SettingsTab.jsx so that file only
// exports components (react-refresh) and so they are unit-testable.
//
// The settings tab stays generic — every registered key is still rendered —
// but the V6 keys (xp.*, gems.*, leaderboard.*) are grouped under Persian
// headings with a label and one line of help each, the prize arrays get a
// per-rank editor instead of a JSON textarea, and every number is checked
// against the registry's own bounds (backend-nest settings.registry.ts)
// before it goes on the wire. The server still validates everything.
//
// The owner's switches are the other half: flipping one changes what every
// live learner sees, and the task protocol says only the owner decides it.
// Those rows carry a standing warning and save only after the key's name is
// typed back — never by a single click.

import { faDigits } from '../../lib/numerals'

/** The groups the tab shows, in order. A key belongs to the first group whose `keys` lists it; the rest go to `other`. */
export const SETTING_GROUPS = [
  {
    id: 'xp',
    title: 'فرمول XP',
    help: 'XP هر تمرین = نمرهٔ هوش مصنوعی (۰ تا ۱۰۰، فقط بهترین تلاش هر جمله در روز) + امتیاز اجتماعی از رأی‌ها؛ هر رأی معتبر هم به رأی‌دهنده XP می‌دهد. XP فقط برای رتبه‌بندی است و خرج نمی‌شود.',
    keys: ['xp.formulaV2Enabled', 'xp.bestTakePerSentencePerDay', 'xp.socialMax', 'xp.socialPointsPerStar', 'xp.socialConfidenceVotes', 'xp.votePoints', 'xp.voteDailyCap'],
  },
  {
    id: 'gems',
    title: 'جم و قیمت‌ها',
    help: 'جم ارز قابل‌خرج است و باید کمیاب بماند: با تمرین روزانه به دست می‌آید و در فروشگاه خرج می‌شود. همهٔ این‌ها فقط وقتی اثر دارند که «اقتصاد جم» روشن باشد.',
    keys: ['gems.enabled', 'gems.dailyPractices', 'gems.dailyPracticeGems', 'gems.repairPrice', 'gems.avatarPrices', 'gems.coinsPerGem'],
  },
  {
    id: 'freeze',
    title: 'فریز استریک',
    help: 'فریز یک روزِ ازدست‌رفته را خودکار پوشش می‌دهد تا استریک نشکند. قیمت و سقف نگهداری اینجاست (فقط با اقتصاد جم روشن).',
    keys: ['gems.freezePrice', 'gems.freezeMax'],
  },
  {
    id: 'leaderboard',
    title: 'رتبه‌بندی',
    help: 'دوره‌های تقویمی (هفتهٔ شنبه تا جمعه و ماه شمسی، به وقت تهران) و دسته‌های رتبه‌بندی.',
    keys: ['leaderboard.calendarPeriodsEnabled', 'leaderboard.size', 'leaderboard.improvedMinAttempts', 'leaderboard.teamsExcludeTeammateVotes'],
  },
  {
    id: 'prizes',
    title: 'جوایز و تسویهٔ دوره',
    help: 'در پایان هر هفته و ماه، برترین‌های جدول XP (و اعضای تیم اول هفته) جم و نشان می‌گیرند. طول هر فهرست = تعداد رتبه‌های برنده. پیش‌نمایش هر تسویه در تب «جوایز».',
    keys: ['leaderboard.settleEnabled', 'leaderboard.weeklyPrizeGems', 'leaderboard.monthlyPrizeGems', 'leaderboard.teamWeeklyPrizeGems'],
  },
  {
    id: 'repeat',
    title: 'تکرار پنج‌باره و انتشار در فید (R13)',
    help: 'تصمیم مالک ۲۰۲۶-۱۰-۱۳: هر جمله چند بار با متن زنده تکرار می‌شود، فقط تکرار آخر به Azure می‌رود، نمره و صدایش خودکار در فید منتشر می‌شود و فید هر روز خالی می‌شود. همه با «روشن کن» مالک و پس از انتشار اپ و تأیید متن رضایت روشن می‌شوند.',
    keys: ['practice.repeatCount', 'practice.repeatMatchThreshold', 'practice.enforceRepeats', 'social.autoPost', 'social.feedDailyReset', 'social.feedPurgeMode'],
  },
  {
    id: 'listen',
    title: 'گوش دادن پیش از تشویق و امتیاز',
    help: 'قاعدهٔ مالک (۲۰۲۶-۱۰-۱۴): تشویق و امتیاز به پست فقط بعد از پخش صدای آن تا آخر. سرور فقط می‌داند هر کاربر صدا را کی گرفت؛ تشویق یا امتیاز را تا گذشتن دست‌کم به اندازهٔ طول صدا از آن لحظه نمی‌پذیرد (listen_first).',
    keys: ['social.requireListenToRate', 'social.requireListenToClap'],
  },
  {
    id: 'transcript',
    title: 'قفل متن درس و کسر امتیاز',
    help: 'قاعدهٔ مالک (۲۰۲۶-۱۰-۱۴): متن جمله در تماشا و تمرین تا سه تلاش ناموفق در همان درس بسته است. تمرینی که با متن باز انجام شود فقط این سهم از XP را می‌گیرد؛ همان تلاشی که متن را باز کرد امتیاز کامل می‌گیرد. سکه دست نمی‌خورد.',
    keys: ['practice.transcriptUnlockXpFactor'],
  },
  {
    id: 'auth',
    title: 'ورود با پیامک و محافظ هزینهٔ پیامک خارجی',
    help: 'کد ورود شماره‌های ایرانی با SMS.ir و ارزان است و هیچ‌کدام از این محدودیت‌ها شاملش نمی‌شود. کد شماره‌های خارجی از اعتبار پیش‌پرداخت Vonage (۰٫۰۳ تا ۰٫۱۸ یورو هر پیامک) کم می‌شود؛ این‌ها جلوی خالی شدنش را می‌گیرند.',
    keys: ['auth.intlOtpDailyCap', 'auth.intlOtpCapKnownExempt', 'auth.intlOtpPerIpPerHour', 'auth.intlOtpAbuseNumbersPerHour', 'auth.intlOtpAbuseBlockHours', 'auth.alertPhones', 'auth.activeCheckTtlSeconds'],
  },
  {
    id: 'app',
    title: 'نسخهٔ اپ اندروید',
    help: 'برای هر فروشگاه جدا (مستقیم، بازار، پلی): اپ پایین‌تر از «حداقل» صفحهٔ قفل «به‌روزرسانی لازم است» می‌بیند، پایین‌تر از «آخرین» روزی یک بار یک خط «نسخهٔ تازه». عدد = versionCode، مثلاً 28؛ null = خاموش. فقط اپ‌های vc28 به بعد این را می‌خوانند.',
    keys: ['app.minVersionCode.direct', 'app.minVersionCode.bazaar', 'app.minVersionCode.play', 'app.latestVersionCode.direct', 'app.latestVersionCode.bazaar', 'app.latestVersionCode.play'],
  },
]

export const OTHER_GROUP = { id: 'other', title: 'سایر تنظیمات', help: null }

/**
 * Per-key Persian label, help and client-side bounds (mirroring the
 * registry's Zod schema). `type`: `int` | `number` | `ranks` (an int array
 * indexed by rank) | `priceMap` (avatar id → int).
 */
export const KEY_SPECS = {
  'auth.intlOtpDailyCap': { label: 'سقف روزانهٔ کد ورود به شماره‌های خارجی', help: 'در هر ۲۴ ساعت، روی همهٔ کاربران. بعد از آن شماره‌های خارجی «سرویس موقتاً در دسترس نیست» می‌بینند و یک پیامک هشدار می‌رود. صفر = بدون سقف.', type: 'int', min: 0, max: 100000, unit: 'کد' },
  'auth.intlOtpCapKnownExempt': { label: 'کاربران قبلی از سقف روزانه معاف باشند', help: 'روشن: شمارهٔ خارجی‌ای که قبلاً یک بار وارد شده، حتی وقتی سقف پر است کد می‌گیرد و در سقف هم شمرده نمی‌شود. اگر حمله سقف را پر کند، فقط تازه‌واردها پشت در می‌مانند.' },
  'auth.intlOtpPerIpPerHour': { label: 'سقف کد خارجی هر آی‌پی در ساعت', help: 'روی همهٔ شماره‌هایی که از یک آی‌پی درخواست می‌شوند. صفر = بدون محدودیت.', type: 'int', min: 0, max: 1000, unit: 'کد' },
  'auth.intlOtpAbuseNumbersPerHour': { label: 'بستن خودکار آی‌پی بعد از این تعداد شمارهٔ خارجی تازه در ساعت', help: 'شماره‌هایی که حساب ندارند. رفتار رباتِ حملهٔ پیامکی است، نه آدم. صفر = بستن خودکار خاموش.', type: 'int', min: 0, max: 100, unit: 'شماره' },
  'auth.intlOtpAbuseBlockHours': { label: 'مدت بسته ماندن آی‌پی', help: 'در این مدت آن آی‌پی برای هیچ شمارهٔ خارجی کد نمی‌گیرد؛ شماره‌های ایرانی آزادند.', type: 'int', min: 1, max: 720, unit: 'ساعت' },
  'auth.alertPhones': { label: 'شماره‌های هشدار سقف پیامک', help: 'فقط موبایل ایرانی، مثل ["+989121234567"]. وقتی سقف روزانه پر شود، روزی یک پیامک می‌گیرند که می‌گوید حمله بوده یا رشد واقعی و چه باید کرد. خالی = هیچ پیامکی نمی‌رود.' },
  'auth.activeCheckTtlSeconds': { label: 'فاصلهٔ بررسی فعال بودن حساب', help: 'کاربری که از پنل غیرفعال شود، حداکثر این‌قدر با توکن قبلی‌اش کار می‌کند. صفر = بررسی در هر درخواست.', type: 'int', min: 0, max: 3600, unit: 'ثانیه' },
  'xp.formulaV2Enabled': { label: 'فرمول تازهٔ XP (نسخهٔ ۲)', help: 'روشن: XP = نمرهٔ هوش مصنوعی + امتیاز اجتماعی و XP رأی. خاموش: همان ۱ تا ۱۰ XP امروز.' },
  'xp.bestTakePerSentencePerDay': { label: 'فقط بهترین تلاش هر جمله در روز', help: 'تلاش بهتر فقط تفاضل را می‌گیرد؛ تلاش بدتر هیچ. خاموش: هر تلاش XP کامل می‌گیرد.' },
  'xp.socialMax': { label: 'سقف امتیاز اجتماعی هر تمرین', help: 'قاعدهٔ کارفرما: ۲۰.', type: 'int', min: 0, max: 1000, unit: 'XP' },
  'xp.socialPointsPerStar': { label: 'امتیاز هر ستاره بالاتر از یک', help: 'پایه = (میانگین ستاره − ۱) × این عدد؛ قاعدهٔ کارفرما: ۵.', type: 'number', min: 0, max: 100 },
  'xp.socialConfidenceVotes': { label: 'ثابت اعتماد رأی‌ها', help: 'پستی با n رأی، n/(n+این عدد) از پایه را نگه می‌دارد؛ قاعدهٔ کارفرما: ۱۰. صفر = بدون تعدیل.', type: 'int', min: 0, max: 10000 },
  'xp.votePoints': { label: 'XP هر رأی معتبر', help: 'فقط نخستین رأی هر کاربر به هر پست؛ قاعدهٔ کارفرما: ۲.', type: 'int', min: 0, max: 100, unit: 'XP' },
  'xp.voteDailyCap': { label: 'سقف رأی‌های XPدار در روز', help: 'رأی‌های بعدی برای پست حساب می‌شوند ولی به رأی‌دهنده XP نمی‌دهند؛ قاعدهٔ کارفرما: ۱۰.', type: 'int', min: 0, max: 1000, unit: 'رأی' },

  'gems.enabled': { label: 'اقتصاد جم', help: 'روشن: سکه دیگر افزایش نمی‌یابد، قیمت‌ها به جم، فریز با سقف. خاموش: همان فروشگاه سکه‌ای امروز.' },
  'gems.dailyPractices': { label: 'تمرین لازم برای جم روزانه', help: 'چندمین تمرین نمره‌دار روز جم می‌دهد؛ قاعدهٔ کارفرما: ۵.', type: 'int', min: 1, max: 100, unit: 'تمرین' },
  'gems.dailyPracticeGems': { label: 'جم هدف تمرین روزانه', help: 'یک بار در هر روز تهران؛ قاعدهٔ کارفرما: ۱.', type: 'int', min: 0, max: 100, unit: 'جم' },
  'gems.repairPrice': { label: 'قیمت ترمیم استریک', help: 'در قاعدهٔ کارفرما نیست؛ پیش‌فرض = قیمت سکه‌ای ÷ نرخ تبدیل.', type: 'int', min: 0, max: 10000, unit: 'جم' },
  'gems.avatarPrices': { label: 'قیمت آواتارها به جم', help: 'مثلاً {"mint": 3}. آواتاری که اینجا نیست = قیمت سکه‌ای ÷ نرخ تبدیل (حداقل ۱).', type: 'priceMap', min: 0, max: 100000 },
  'gems.coinsPerGem': { label: 'نرخ تبدیل سکه به جم', help: 'چند سکه = یک جم (پیش‌فرض Q5: ۱۰۰). برای مهاجرت موجودی‌ها و قیمت آواتارهای بی‌قیمت.', type: 'int', min: 1, max: 100000, unit: 'سکه' },

  'gems.freezePrice': { label: 'قیمت هر فریز', help: 'قاعدهٔ کارفرما: ۵ جم.', type: 'int', min: 0, max: 10000, unit: 'جم' },
  'gems.freezeMax': { label: 'سقف نگهداری فریز', help: 'بیشترین فریز در کیف و بیشترین مصرف برای یک فاصله؛ قاعدهٔ کارفرما: ۳.', type: 'int', min: 1, max: 100, unit: 'فریز' },

  'leaderboard.calendarPeriodsEnabled': { label: 'رتبه‌بندی تقویمی برای اپ‌های منتشرشده', help: 'روشن: جدول هفته/ماه اپ و وب از پنجرهٔ غلتان ۷/۳۰ روزه به هفته و ماه تقویمی می‌رود.' },
  'leaderboard.size': { label: 'تعداد ردیف‌های هر جدول', help: 'ردیف خود کاربر هر جا که باشد اضافه می‌شود.', type: 'int', min: 1, max: 200, unit: 'ردیف' },
  'leaderboard.improvedMinAttempts': { label: 'حداقل تلاش برای «بیشترین پیشرفت»', help: 'در هر دو دورهٔ جاری و قبلی (Q6).', type: 'int', min: 1, max: 1000, unit: 'تلاش' },
  'leaderboard.teamsExcludeTeammateVotes': { label: 'رأی هم‌تیمی در «بهترین تیم‌ها» حساب نشود', help: 'Q1: رأی هم‌تیمی برای امتیاز فرد حساب می‌شود، برای تیم نه.' },

  'leaderboard.settleEnabled': { label: 'پرداخت جوایز دوره', help: 'روشن (و با اقتصاد جم روشن): تسویه واقعاً جم و نشان می‌دهد. خاموش: فقط پیش‌نمایش.' },
  'leaderboard.weeklyPrizeGems': { label: 'جوایز هفتگی (جدول XP)', help: 'قاعدهٔ کارفرما: ۱۰، ۵، ۳. رتبهٔ ۱ نشان هفتگی هم می‌گیرد.', type: 'ranks', min: 0, max: 10000, maxRanks: 50, unit: 'جم' },
  'leaderboard.monthlyPrizeGems': { label: 'جوایز ماهانه (جدول XP)', help: 'قاعدهٔ کارفرما: ۳۰، ۱۵، ۸. رتبهٔ ۱ نشان ماهانه هم می‌گیرد.', type: 'ranks', min: 0, max: 10000, maxRanks: 50, unit: 'جم' },
  'practice.repeatCount': { label: 'تعداد تکرار هر جمله', help: 'فقط تکرار آخر به Azure می‌رود و نمره می‌گیرد. ۱ = همان یک ضبط امروز؛ قاعدهٔ مالک: ۵.', type: 'int', min: 1, max: 10, unit: 'تکرار' },
  'practice.enforceRepeats': { label: 'رد ارسالِ بدون تکرار کافی در سرور', help: 'روشن: سرور تمرینی را که اپ برایش کمتر از «تعداد تکرار − ۱» تکرار شمرده، یا تکرار بی‌زمانِ گفتن گزارش کرده، رد می‌کند (repetitions_required). تا اندروید vc28 خاموش بماند: ۰٫۱۳٫۷ تکرار را گزارش نمی‌کند.' },
  'social.requireListenToRate': { label: 'امتیاز فقط پس از گوش دادن', help: 'روشن (پیش‌فرض): اولین امتیاز به هر پست تا گذشتن طول صدایش از لحظهٔ گرفتن آن پذیرفته نمی‌شود؛ امتیازِ داده‌شده را می‌شود عوض کرد.' },
  'social.requireListenToClap': { label: 'تشویق فقط پس از گوش دادن', help: 'همان قاعده برای تشویق (پس گرفتن تشویق همیشه آزاد است). تا اندروید vc28 خاموش بماند: ۰٫۱۳٫x دکمهٔ تشویق را قفل نمی‌کند.' },
  'practice.repeatMatchThreshold': { label: 'حداقل تطابق متن زنده', help: 'از ۲۰۲۶-۱۰-۱۵ (تصمیم مالک «همهٔ کلمات»): وب و اندروید vc28 با هر مقدار بالای ۰ همهٔ کلمه‌های جمله را به ترتیب می‌خواهند (حرف اضافه آزاد، «I\'m»=«I am»، «2»=«two») و این عدد فقط برای اندروید ۰٫۱۳٫x است: سهم کلمه‌ها (۰ تا ۱)، ۰٫۵ = نصف. ۰ = هر تکراری که صدا دارد، همه‌جا (کلید اضطراری اگر تشخیص‌گر لهجه را بد می‌فهمد).', type: 'number', min: 0, max: 1 },
  'social.autoPost': { label: 'انتشار خودکار هر تمرین نمره‌دار در فید', help: 'روشن: نمره و صدای تکرار آخر بی‌دکمه منتشر می‌شود؛ کلاینت‌ها پیش از اولین تمرین رضایت نسخهٔ ۲ را می‌پرسند. خاموش: همان دکمهٔ «ارسال به فید» امروز.' },
  'social.feedDailyReset': { label: 'فید فقط پست‌های امروز', help: 'روشن: از نیمه‌شب تهران پست‌های دیروز در فید، پروفایل و واکنش‌ها دیده نمی‌شوند. تلاش، صدا، نمره و XP دست‌نخورده می‌ماند.' },
  'practice.transcriptUnlockXpFactor': { label: 'سهم XP پس از باز شدن متن', help: '۰٫۵ = نصف امتیاز؛ ۱ = بدون کسر؛ ۰ = بدون XP. نتیجهٔ تمرین دلیلش را نشان می‌دهد.', type: 'number', min: 0, max: 1 },
  'social.feedPurgeMode': { label: 'پاک‌سازی شبانهٔ پست‌های دیروز', help: '«report»: فقط شمارش. «delete»: پست‌های دیروز با تشویق، امتیاز و نظرهایشان واقعاً حذف می‌شوند — برگشت‌ناپذیر.' },

  // Nullable ints: no `type`, so the JSON editor takes a number or null and the server's schema checks it.
  'app.minVersionCode.direct': { label: 'حداقل نسخه — مستقیم (APK)', help: 'versionCode، مثلاً 28؛ null = بدون قفل. دکمهٔ صفحهٔ قفل کانال به‌روزرسانی (latest.json) را دانلود می‌کند: اول همان نسخه را آنجا منتشر کنید.' },
  'app.minVersionCode.bazaar': { label: 'حداقل نسخه — بازار', help: 'versionCode؛ null = بدون قفل. دکمهٔ صفحهٔ قفل صفحهٔ طوطی در بازار را باز می‌کند: فقط پس از تأیید آن نسخه در بازار.' },
  'app.minVersionCode.play': { label: 'حداقل نسخه — گوگل‌پلی', help: 'versionCode؛ null = بدون قفل. دکمهٔ صفحهٔ قفل صفحهٔ طوطی در پلی را باز می‌کند: فقط پس از انتشار آن نسخه در پلی.' },
  'app.latestVersionCode.direct': { label: 'آخرین نسخه — مستقیم (APK)', help: 'در اپ مستقیم اثری ندارد: بنر کانال به‌روزرسانی (latest.json) نسخهٔ تازه را خودش اعلام می‌کند.' },
  'app.latestVersionCode.bazaar': { label: 'آخرین نسخه — بازار', help: 'versionCode؛ null = بدون پیام.' },
  'app.latestVersionCode.play': { label: 'آخرین نسخه — گوگل‌پلی', help: 'versionCode؛ null = بدون پیام.' },

  'leaderboard.teamWeeklyPrizeGems': { label: 'جایزهٔ هر عضو تیم برتر هفته', help: 'به هر عضو تیم آن رتبه؛ قاعدهٔ کارفرما: فقط تیم اول، ۵ جم. فهرست خالی = بدون جایزهٔ تیمی.', type: 'ranks', min: 0, max: 10000, maxRanks: 20, unit: 'جم' },
}

export function keySpec(key) {
  return KEY_SPECS[key] ?? null
}

/**
 * Split the registry's items into the groups above, keeping each group's own
 * key order and the registry's order for the rest. Empty groups are dropped.
 */
export function groupSettings(items) {
  const byKey = new Map(items.map(item => [item.key, item]))
  const claimed = new Set()
  const groups = []
  for (const group of SETTING_GROUPS) {
    const members = group.keys.filter(key => byKey.has(key)).map(key => byKey.get(key))
    members.forEach(item => claimed.add(item.key))
    if (members.length > 0) groups.push({ ...group, items: members })
  }
  const rest = items.filter(item => !claimed.has(item.key))
  if (rest.length > 0) groups.push({ ...OTHER_GROUP, items: rest })
  return groups
}

// ─── Owner switches ─────────────────────────────────────────────────────────

/** Switches only the owner turns (phase-2 protocol); every `social.*` key is treated the same. */
export const OWNER_SWITCHES = ['xp.formulaV2Enabled', 'gems.enabled', 'leaderboard.calendarPeriodsEnabled', 'leaderboard.settleEnabled', 'teams.enabled', 'practice.repeatCount', 'practice.enforceRepeats', 'social.requireListenToClap', 'app.minVersionCode.direct', 'app.minVersionCode.bazaar', 'app.minVersionCode.play']

export function isOwnerSwitch(key) {
  return OWNER_SWITCHES.includes(key) || key.startsWith('social.')
}

/** What flipping an owner switch does to live learners — shown on the row before anything is clicked. */
const OWNER_CONSEQUENCES = {
  'xp.formulaV2Enabled': 'XP همهٔ تمرین‌های تازه با فرمول نسخهٔ ۲ حساب و در دفتر XP ثبت می‌شود. پیش از آن بازمحاسبهٔ XP (V6-b-migrate) باید با تأیید مالک اجرا شده باشد.',
  'gems.enabled': 'فروشگاه همهٔ کاربران از سکه به جم می‌رود و سکه دیگر افزایش نمی‌یابد. پیش از آن تبدیل سکه به جم (V6-b-migrate) باید با تأیید مالک اجرا شده باشد.',
  'leaderboard.calendarPeriodsEnabled': 'جدول هفته و ماه اپ‌های منتشرشده و وب از پنجرهٔ غلتان به دورهٔ تقویمی می‌رود؛ بدون فرمول XP نسخهٔ ۲ جدول‌ها خالی می‌شوند.',
  'leaderboard.settleEnabled': 'تسویهٔ دوره (دکمه و timerها) واقعاً جم و نشان می‌دهد و به برندگان اعلان می‌فرستد؛ پرداخت از پنل برگشت‌پذیر نیست.',
  'teams.enabled': 'تیم‌ها برای همهٔ کاربران نمایش داده می‌شود و آنبوردینگ کاربر تازه را در تیم می‌نشاند.',
  'practice.repeatCount': 'مسیر تمرین همهٔ کاربران وب و اپ‌های تازه عوض می‌شود: هر جمله این‌قدر تکرار و فقط آخری فرستاده می‌شود. پیش از آن مدل Vosk باید روی سرور باشد.',
  'social.autoPost': 'از این لحظه هر تمرین نمره‌دارِ کاربرانی که رضایت نسخهٔ ۲ داده‌اند با صدا در فید منتشر می‌شود و از بقیه پیش از تمرین رضایت خواسته می‌شود. متن رضایت باید تأییدشده باشد (Q11).',
  'social.feedDailyReset': 'پست‌های پیش از امروز (تهران) برای همه از فید، پروفایل‌ها و واکنش‌ها کنار می‌روند.',
  'practice.enforceRepeats': 'سرور هر ارسالی را که تکرار کافی گزارش نکند رد می‌کند. اندروید ۰٫۱۳٫۷ تکرار را گزارش نمی‌کند: تا vc28 حداقل نسخهٔ در دست کاربران نشده، روشن نکنید.',
  'social.requireListenToClap': 'تشویقِ پیش از پخش کامل صدا رد می‌شود. اندروید ۰٫۱۳٫x دکمهٔ تشویق را قفل نمی‌کند و کاربرش خطا می‌بیند: تا vc28 روشن نکنید.',
  'app.minVersionCode.direct': 'هر اپ مستقیمِ پایین‌تر از این عدد (از vc28 به بعد) تا به‌روزرسانی وارد نمی‌شود. نسخهٔ لازم باید پیش‌تر در کانال به‌روزرسانی (latest.json) منتشر شده باشد.',
  'app.minVersionCode.bazaar': 'هر اپ بازارِ پایین‌تر از این عدد (از vc28 به بعد) تا به‌روزرسانی از بازار وارد نمی‌شود. نسخهٔ لازم باید در بازار تأیید شده باشد.',
  'app.minVersionCode.play': 'هر اپ پلیِ پایین‌تر از این عدد (از vc28 به بعد) تا به‌روزرسانی از گوگل‌پلی وارد نمی‌شود. نسخهٔ لازم باید در پلی منتشر شده باشد.',
  'social.feedPurgeMode': 'با «delete» کار شبانه پست‌های دیروز را برای همیشه حذف می‌کند؛ اول یک اجرای «report» را ببینید.',
}
const SOCIAL_CONSEQUENCE = 'حریم اجتماعی کاربران زنده را تغییر می‌دهد (فید، عکس، پروفایل عمومی).'

export function ownerSwitchNote(key) {
  if (!isOwnerSwitch(key)) return null
  return OWNER_CONSEQUENCES[key] ?? SOCIAL_CONSEQUENCE
}

/** The typed confirmation of an owner switch: the key's own name, exactly. */
export function ownerConfirmMatches(key, typed) {
  return typeof typed === 'string' && typed.trim() === key
}

// ─── Validation ────────────────────────────────────────────────────────────

const faNum = faDigits

function checkNumber(n, spec, what = 'مقدار') {
  if (!Number.isFinite(n)) throw new Error(`${what} باید عدد باشد.`)
  if (spec.type !== 'number' && !Number.isInteger(n)) throw new Error(`${what} باید عدد صحیح باشد.`)
  if (spec.min != null && n < spec.min) throw new Error(`${what} نباید کمتر از ${faNum(spec.min)} باشد.`)
  if (spec.max != null && n > spec.max) throw new Error(`${what} نباید بیشتر از ${faNum(spec.max)} باشد.`)
}

/**
 * Check a value about to be saved against the key's spec; throws a Persian
 * message, or returns the value. Keys without a spec (or a spec without a
 * type) pass through — the server validates them.
 */
export function validateSettingValue(key, value) {
  const spec = keySpec(key)
  if (!spec?.type) return value
  if (spec.type === 'int' || spec.type === 'number') {
    checkNumber(value, spec)
    return value
  }
  if (spec.type === 'ranks') {
    if (!Array.isArray(value)) throw new Error('فهرست جوایز باید آرایه‌ای از عددها باشد، مثلاً [10, 5, 3].')
    if (value.length > spec.maxRanks) throw new Error(`حداکثر ${faNum(spec.maxRanks)} رتبه.`)
    value.forEach((n, i) => checkNumber(n, spec, `جایزهٔ رتبهٔ ${faNum(i + 1)}`))
    return value
  }
  if (spec.type === 'priceMap') {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new Error('قیمت آواتارها باید یک شیء JSON باشد، مثلاً {"mint": 3}.')
    for (const [id, n] of Object.entries(value)) {
      if (!/^[a-z0-9_-]{1,40}$/.test(id)) throw new Error(`شناسهٔ آواتار «${id}» معتبر نیست (فقط حروف کوچک لاتین، رقم، - و _).`)
      checkNumber(n, spec, `قیمت «${id}»`)
    }
    return value
  }
  return value
}

// ─── The per-rank editor's text ─────────────────────────────────────────────
//
// The row keeps one string of editor state for every kind; for a prize array
// that string is a JSON array of the inputs' *strings*, so a half-typed or
// empty rank survives until save, where `parseRanks` turns it into numbers.

export function ranksToText(value) {
  return JSON.stringify((Array.isArray(value) ? value : []).map(n => String(n)))
}

export function ranksFromText(text) {
  try {
    const parsed = JSON.parse(text)
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch { return [] }
}

/** The ranks' strings → numbers, refusing an empty or non-numeric rank in Persian. */
export function parseRanks(text) {
  return ranksFromText(text).map((raw, i) => {
    const trimmed = raw.trim()
    if (trimmed === '') throw new Error(`جایزهٔ رتبهٔ ${faNum(i + 1)} خالی است.`)
    const n = Number(trimmed)
    if (Number.isNaN(n)) throw new Error(`جایزهٔ رتبهٔ ${faNum(i + 1)} عدد نیست.`)
    return n
  })
}
